<template>
  <div>
    <alert v-if="loading" class="shadow-card" />
    <div v-else-if="unavailable">
      <div class="container">
        <div class="alert alert-danger mt-3">
          The meeting room you are trying to access is not available. You may
          not have access to it, or the room no longer exists.
          <router-link to="/" class="alert-link">Back to dashboard</router-link>
        </div>
      </div>
    </div>
    <div v-else class="container">
      <div class="row my-2">
        <div class="col-12 col-md-8 text-truncate">
          <h1>{{ roomName || "Meeting" }} Room</h1>
        </div>
        <div class="col text-md-right">
          <button class="btn btn-outline-blue" @click="copyMeetingLink">
            <span v-if="copied">link copied!</span>
            <span v-else> Invite Participant </span>
          </button>
        </div>
      </div>
      <div v-if="resolvedRoomUrl" class="whereby-container">
        <whereby-embed
          :room="resolvedRoomUrl"
          class="meeting-container shadow-card rounded overflow-hidden"
        ></whereby-embed>
        <button
          class="btn btn-e27c1d oneai-button"
          @click="checkScribeAiTerms"
          :disabled="engagementModalOpenState"
          v-if="
            roomDetails &&
            roomDetails.is_host &&
            roomDetails.host.id == user.id &&
            roomDetails.appointment
          "
        >
          Start AI Scribe
        </button>
      </div>
    </div>
    <!-- Terms Modal -->
    <modal
      class="scribe-ai-terms-modal"
      name="scribe-ai-terms-modal"
      transition="pop-out"
      :clickToClose="false"
      height="auto"
      v-model="isScribeTermsAIModalOpen"
    >
      <div class="position-relative main-modal-container">
        <div class="vm--top-right-slot" style="z-index: 1">
          <button
            class="btn p-0 p-1 mt-3 mr-3 border-0"
            @click="closeScribeAIModal"
          >
            <i class="fas fa-times fa-lg"></i>
          </button>
        </div>
        <div class="card-body pt-0">
          <h1 class="text-center mt-0 mb-3 font-weight-bold">
            Start using AI Scribe
          </h1>
          <p class="text-center text-muted">
            Start using C3's AI Scribe powered by OneChart.
          </p>

          <badge class="badge-terms text-dark d-flex align-items-center p-4">
            <input
              id="termsCheckbox"
              class="pointer-cursor terms-input"
              type="checkbox"
              v-model="termsAccepted"
            />
            <label
              for="termsCheckbox"
              class="pointer-cursor mb-0 pl-3 text-left"
            >
              I agree to the
              <a
                class="text-primary"
                href="https://onechart.ai/privacy"
                target="_blank"
                >terms and conditions</a
              >
              and
              <a
                class="text-primary"
                href="https://onechart.ai/privacy"
                target="_blank"
                >privacy policy</a
              >
            </label>
          </badge>

          <badge
            class="badge-terms px-3 pt-4 pb-3 mt-4 w-100 text-left text-dark"
          >
            <p class="mb-0 font-weight-bold">
              I agree for C3 to share the following information to OneChart:
            </p>
            <ul class="d-flex flex-wrap mt-3 pt-1 mb-0">
              <li
                class="col-6 mb-2"
                v-for="field in [
                  'Email',
                  'ID',
                  'Name',
                  'Specialty Type',
                  'Practice Group',
                  'Phone Number',
                  'Region',
                ]"
                :key="field"
              >
                {{ field }}
              </li>
            </ul>
          </badge>

          <button
            class="btn btn-primary w-100 mt-4"
            :disabled="!termsAccepted"
            @click="acceptScribeAIModal"
          >
            <i class="fas fa-play-circle mr-2"></i>Start Using AI Scribe
          </button>

          <button
            class="btn btn-link text-black font-weight-bold w-100 mt-3"
            @click="closeScribeAIModal"
          >
            Maybe Later
          </button>
        </div>
      </div>
    </modal>

    <!-- Upgrade Modal -->
    <modal
      class="scribe-ai-upgrade-modal"
      name="scribe-ai-upgrade-modal"
      transition="pop-out"
      :clickToClose="false"
      height="auto"
      v-model="isScribeUpgradeAIModalOpen"
    >
      <div class="position-relative main-modal-container">
        <div class="vm--top-right-slot" style="z-index: 1">
          <button
            class="btn p-0 p-1 mt-3 mr-3 border-0"
            @click="!isNotifyingAdmin && closeUpgradeModal()"
          >
            <i class="fas fa-times fa-lg"></i>
          </button>
        </div>
        <div class="card-body pt-0">
          <h1 class="text-center mt-0 mb-3 font-weight-bold">
            <i
              class="fas fa-2x fa-exclamation-triangle mr-2 text-deep-warning"
            ></i>
          </h1>
          <h1 class="text-center">Note Limit Reached</h1>

          <div class="alert alert5-warning text-center p-4 font-weight-bold">
            <h1 class="mb-2" style="font-size: 32px">
              {{ notesInfo.remaining }}
            </h1>
            <div>
              note{{ notesInfo.remaining === 1 ? "" : "s" }} left for your
              organization
            </div>
          </div>

          <p class="text-center text-muted mb-4 px-md-3">
            Your organization is approaching its AI Scribe note limit.
            <br />
            Contact your administrator to purchase additional notes and continue
            using this feature.
          </p>

          <button
            class="btn btn-primary w-100 mt-4"
            :disabled="isNotifyingAdmin"
            @click="notifyAdminForUpgrade()"
          >
            <i class="fas fa-bell mr-2" v-if="!isNotifyingAdmin"></i>
            <i class="fa fa-spin fa-circle-notch mr-2" v-else></i>
            Notify Administrator
          </button>

          <button
            class="btn btn-link text-black font-weight-bold w-100 mt-3"
            :disabled="isNotifyingAdmin"
            @click="closeUpgradeModal()"
          >
            Maybe Later
          </button>
        </div>
      </div>
    </modal>

    <sidebar
      :toggle="engagementModalOpenState"
      @close="closeEngagementModal"
      class="create-engagement-modal"
      :class="`${engagementModalMinimized ? 'minimized' : 'maximized'}`"
    >
      <div
        class="
          size-btns-container
          d-flex
          justify-content-between
          align-items-center
        "
      >
        <h3 class="mb-0">AI Scribe</h3>
        <div>
          <button
            class="btn minimize-btn"
            v-if="!engagementModalMinimized"
            @click="minimizeEngagementModal()"
            title="minimize"
          >
            <i class="fas fa-lg fa-minus"></i>
          </button>
          <button
            class="btn maximize-btn"
            :disabled="!engagementModalMinimized"
            @click="maximizeEngagementModal()"
            title="maximize"
          >
            <i class="fas fa-lg fa-expand"></i>
          </button>
        </div>
      </div>
      <notes-list
        v-if="
          engagementModalOpenState &&
          roomDetails &&
          roomDetails.host.id == user.id &&
          roomDetails.appointment
        "
        :userId="roomDetails.appointment.user_2.id"
        :meeting="roomDetails"
        :newOnly="true"
        @engagementNoteAdded="closeEngagementModal"
        @engagementNoteDeleted="closeEngagementModal"
      ></notes-list>
    </sidebar>

    <floating-upload-manager />
  </div>
</template>
<script>
import "@whereby.com/browser-sdk";
import Swal from "sweetalert2";
import { mapActions, mapState } from "vuex";
import FloatingUploadManager from "../../components/FloatingUploadManager.vue";
import NotesList from "../patients/profile/engagement-notes/List";
export default {
  components: {
    NotesList,
    FloatingUploadManager,
  },
  data() {
    return {
      isScribeTermsAIModalOpen: false,
      isScribeUpgradeAIModalOpen: false,
      loading: true,
      resolvedRoomUrl: null,
      unavailable: false,
      copied: false,
      termsAccepted: false,
      isNotifyingAdmin: false,
      engagementModalOpenState: false,
      engagementModalMinimized: false,
      roomDetails: null,
    };
  },
  created() {
    this.getOneChartNotesInfo();
    this.getRoomBySlugId({ slugId: this.roomSlug, roomKey: this.roomKey }).then(
      (data) => {
        this.loading = false;
        if (data) {
          this.roomDetails = data;
          this.resolvedRoomUrl = data.room_url;
        } else {
          this.unavailable = true;
        }
      }
    );
  },
  computed: {
    ...mapState({
      user: (state) => state.auth.user || {},
      notesInfo: (state) => state.oneai.oneChartNotesInfo || {},
    }),
    roomSlug: function () {
      return this.$route.params.slug;
    },
    roomKey: function () {
      return this.$route.query.roomKey;
    },
    roomName: function () {
      return this.$route.query.roomName;
    },
  },
  methods: {
    ...mapActions({
      getRoomBySlugId: "rooms/getRoomBySlugId",
      getOneChartNotesInfo: "oneai/getOneChartNotesInfo",
    }),
    copyMeetingLink: function () {
      const vm = this;
      let url = this.roomDetails?.participant_short_url;
      if (!url) {
        url = window.location.href.split("?")[0];
        if (this.roomName) {
          url += `?roomName=${encodeURIComponent(this.roomName)}`;
        }
      }
      vm.copied = true;
      navigator.clipboard.writeText(url).then(
        function () {
          setTimeout(() => {
            vm.copied = null;
          }, 700);
        },
        function (err) {
          console.error("Async: Could not copy text: ", err);
        }
      );
    },
    checkScribeAiTerms() {
      if (!this.notesInfo.remaining) {
        this.openUpgradeModal();
        return;
      }
      this.termsAccepted = !!localStorage.getItem("AI_SCRIBE_TERMS_ACCEPTED");
      if (this.termsAccepted) {
        this.openCreateEngagementModal();
      } else {
        this.isScribeTermsAIModalOpen = true;
      }
    },
    closeScribeAIModal() {
      this.isScribeTermsAIModalOpen = false;
    },
    openUpgradeModal() {
      this.isScribeUpgradeAIModalOpen = true;
    },
    closeUpgradeModal() {
      this.isScribeUpgradeAIModalOpen = false;
    },
    acceptScribeAIModal() {
      if (this.termsAccepted) {
        localStorage.setItem("AI_SCRIBE_TERMS_ACCEPTED", true);
        this.closeScribeAIModal();
        this.openCreateEngagementModal();
      }
    },
    openCreateEngagementModal() {
      this.engagementModalOpenState = true;
    },
    closeEngagementModal() {
      this.engagementModalOpenState = false;
    },
    notifyAdminForUpgrade() {
      if (this.isNotifyingAdmin) {
        return;
      }
      this.isNotifyingAdmin = true;
      this.notifyAdminforOneAiUpgrade().then((success) => {
        this.isNotifyingAdmin = false;
        if (success) {
          Swal.fire({
            title: "Notification Sent",
            text: "Your administrator has been notified about the AI Scribe upgrade.",
            icon: "success",
          });
          this.closeUpgradeModal();
        }
      });
    },
    minimizeEngagementModal() {
      this.engagementModalMinimized = true;
    },
    maximizeEngagementModal() {
      this.engagementModalMinimized = false;
    },
  },
};
</script>
<style lang="scss" scoped>
.badge-terms {
  white-space: normal;
}
.meeting-container {
  height: calc(100vh - 70px);
}

.whereby-container {
  position: relative;

  .oneai-button {
    position: absolute;
    bottom: 10px;
    right: 10px;
    z-index: 1000;
  }
}

.badge-terms {
  font-size: 15px;
  background-color: #eff5fb;

  .terms-input {
    min-width: 18px;
    min-height: 18px;
  }
}
.scribe-ai-terms-modal,
.scribe-ai-upgrade-modal {
  .v--modal {
    max-height: 80vh;
  }
}
</style>

<style lang="scss">
.create-engagement-modal {
  &.minimized {
    .tray.open {
      width: 190px;
      height: 43px;
      overflow: hidden;
      bottom: 0;
      top: auto;

      .close {
        margin-top: 0 !important;
      }
      .size-btns-container {
        margin-top: 2px;
      }
      .tray-body {
        padding-top: 0;
      }
    }
  }
  .tray-bg {
    display: none;
  }
  .close,
  .minimize-btn,
  .maximize-btn {
    opacity: 1;
    transition: all 0.2s ease-in-out;

    &:hover {
      opacity: 0.8;
    }
  }
  .size-btns-container {
    text-align: right;
    margin-top: 4px;
    padding-right: 8px;
  }
  .notes-list-container {
    margin-top: 12px;
    padding: 0 !important;

    .scribe-action-btn {
      margin-top: 12px !important;
      margin-left: auto;
      margin-right: auto !important;
    }
    .external-audio-result,
    .audio-recorder-container {
      width: 100%;
      margin-top: 12px !important;
    }
    .audio-recorder-outer-container {
      margin-left: auto !important;
      margin-right: auto;
    }
    .controls-badge {
      justify-content: center !important;
    }
    .selects-outer-container {
      width: 100%;
    }
    .recording-status {
      width: 100%;
      margin-bottom: 16px;
    }
    .note-card {
      border-radius: 12px !important;
    }
    .note-container {
      border-radius: 12px;
      border: 1px solid var(--theme-color);
    }
    .appointment-select-container,
    .icd-select-container {
      flex: 0 0 100%;
      max-width: 100%;
    }
    .appointment-select-container {
      .col {
        padding-right: 0 !important;
      }
    }
    .icd-select-container {
      padding-left: 0 !important;
    }
    .doc-type-select,
    .lang-select {
      min-width: 50%;
    }
  }
}
</style>
